import { isReactivationDeferred } from "./crm-v2-policy.mjs";
import { weightedCommercialScore, applyCallV2, validateCallV2, v2ResultOptions, isBlockedForProspecting } from "./crm-v2-policy.mjs";
import {
  getNextActionDate,
  latestSignal,
  signalPosts,
  isRecruitmentSignal,
  getLastInteraction,
} from "./priority-policy.mjs";
import {
  todayParis,
  quoteValidityDate,
  formatDate,
  dateValue,
  addBusinessDays,
  isBusinessDay,
  businessDaysBetween,
} from "../utils/calendar.mjs";
import { newId } from "../utils/ids.mjs";
export {
  todayParis,
  formatDate,
  addBusinessDays,
  isBusinessDay,
  businessDaysBetween,
};
export const statuses = [
  "CLIENT ACTIF",
  "À RÉACTIVER",
  "CLIENT DORMANT",
  "À RECONQUÉRIR",
  "PROSPECT",
  "CLIENT PERDU",
];
export const needStatuses = [
  "à qualifier",
  "devis à créer",
  "devis envoyé",
  "négociation",
  "signé",
  "suspendu",
  "perdu",
];
const legacyResultOptions = [
  "Nouveau besoin",
  "À rappeler",
  "Pas de besoin actuellement",
  "Activité en baisse",
  "Travaille avec un concurrent",
  "Pas de réponse / Non joignable",
  "Insatisfaction",
  "Client perdu",
];
export const resultOptions = v2ResultOptions;
export { resultGroups, commercialStatuses, whyCall, crmV2Indicators, isBlockedForProspecting } from "./crm-v2-policy.mjs";
export const reasons = [
  "Baisse d’activité / carnet de commandes",
  "Budget réduit",
  "Réorganisation interne",
  "Travaille davantage avec un concurrent",
  "Fin de projet",
  "Autre",
];
export const lossReasons = [
  "Prix",
  "Concurrent",
  "Délai",
  "Besoin annulé",
  "Besoin pourvu autrement",
  "Aucune réponse",
  "Autre",
];
export const dissatisfactionCategories = [
  "Qualité candidats/intérimaires",
  "Réactivité",
  "Tarifs",
  "Administratif",
  "Facturation",
  "Commercial",
  "Autre",
];
export const contractTypes = ["Intérim", "Vacation", "CDD", "CDI"];
export const currentCommercial = {
  id: "commercial-demo",
  name: "Commercial principal",
  agency: "Toulouse",
};
const uid = () => newId();
const stamp = (now) => new Date(now || Date.now()).toISOString();
const getClient = (s, id) => {
  const c = s.clients.find((c) => c.id === id);
  if (!c) throw new Error("Établissement introuvable.");
  return c;
};
const updateClient = (s, id, patch) => ({
  ...s,
  clients: s.clients.map((c) => (c.id === id ? { ...c, ...patch } : c)),
});
function statusOf(c) {
  const statusKey = value => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase();
  const canonical = statuses.find(status => statusKey(status) === statusKey(c.status));
  if (canonical) return canonical;
  return (
    {
      reactivation: "À RÉACTIVER",
      "activité en baisse": "CLIENT ACTIF",
      "client perdu": "CLIENT PERDU",
      reconquête: "À RECONQUÉRIR",
      insatisfaction: "CLIENT ACTIF",
      maturation: "PROSPECT",
      opportunité: c.kind === "prospect" ? "PROSPECT" : "CLIENT ACTIF",
    }[c.status] || (c.kind === "prospect" ? "PROSPECT" : "CLIENT DORMANT")
  );
}
function validIsoDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const parsed = new Date(`${value}T12:00:00Z`);
  return !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
    ? value
    : "";
}
export function parisDateValue(value) {
  if (value == null || value === "") return "";
  if (
    typeof value === "object" &&
    !(value instanceof Date) &&
    "$date" in value
  )
    return parisDateValue(value.$date);
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    return todayParis(value);
  }
  const text = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(text))
    return validIsoDay(text.split("/").reverse().join("-"));
  const exact = validIsoDay(text);
  if (exact) return exact;
  const localTimestamp = text.match(/^(\d{4}-\d{2}-\d{2})[T ]/);
  if (
    localTimestamp &&
    !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(text)
  )
    return validIsoDay(localTimestamp[1]);
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? "" : todayParis(parsed);
}
export function orderDateValue(order) {
  if (!order || typeof order !== "object") return parisDateValue(order);
  for (const value of [order.date, order.createdAt, order.startDate]) {
    const day = parisDateValue(value);
    if (day) return day;
  }
  const missionDates = [
    ...(Array.isArray(order.dateRanges)
      ? order.dateRanges.flatMap((period) => [
          period?.startDate ?? period?.date,
          period?.endDate,
        ])
      : []),
    ...(Array.isArray(order.selectedDates) ? order.selectedDates : []),
    ...(Array.isArray(order.missionDates) ? order.missionDates : []),
  ]
    .map(parisDateValue)
    .filter(Boolean)
    .sort();
  return missionDates[0] || "";
}
export function normalizeClient(c) {
  let hasPrimary = false;
  const contacts = (
    c.contacts || [
      {
        id: `contact-${c.id}`,
        name: c.contact || "",
        role: c.role || "",
        phone: c.phone || "",
        email: c.email || "",
        active: true,
        primary: true,
      },
    ]
  ).map((x) => {
    const primary = !!x.primary && x.active !== false && !hasPrimary;
    if (primary) hasPrimary = true;
    return {
      ...x,
      firstName: x.firstName || "",
      lastName: x.lastName || x.name || "",
      name: x.name || `${x.firstName || ""} ${x.lastName || ""}`.trim(),
      active: x.active !== false,
      primary,
      inactiveReason: x.inactiveReason || "",
      bestTime: x.bestTime || "",
      unavailableDays: x.unavailableDays || [],
    };
  });
  return {
    ...c,
    establishment_id: c.establishment_id ?? c.id,
    client_id: c.client_id ?? c.id,
    siren: c.siren || String(c.siret || "").slice(0, 9),
    tradeName: c.tradeName || "",
    address: c.address || "",
    postalCode: c.postalCode || "",
    email: c.email || "",
    agency: c.agency || "Toulouse",
    ownerId: c.ownerId || currentCommercial.id,
    owner: c.owner || currentCommercial.name,
    secondaryOwner: c.secondaryOwner || "",
    status: statusOf(c),
    riskReason:
      c.riskReason ||
      (["activité en baisse", "insatisfaction"].includes(c.status)
        ? c.status
        : ""),
    contacts,
    history: c.history || [],
    orders: (c.orders || []).map((order) => ({
      ...order,
      date: orderDateValue(order),
    })),
    contracts: c.contracts || [],
    invoices: c.invoices || [],
    quotes: c.quotes || [],
    closed: !!c.closed,
  };
}
export function normalizeState(saved) {
  const state = {
    ...saved,
    version: 3,
    dailyGoals: {
      callResults: saved.dailyCallGoal || 24,
      decisionMakersReached: 8,
      needsDetected: 3,
      quotesSent: 2,
      ...(saved.dailyGoals || {}),
    },
    workday: {
      start: "09:00",
      end: "18:00",
      lunchStart: "12:30",
      lunchEnd: "13:30",
      ...(saved.workday || {}),
    },
    goalHistory: saved.goalHistory || [],
    goalsByUser: saved.goalsByUser || {},
    clients: saved.clients.map(normalizeClient),
    signals: saved.signals || [],
    quotes: saved.quotes || [],
    events: saved.events || [],
    recruitmentNeeds: saved.recruitmentNeeds || [],
    engagements: saved.engagements || [],
    imports: saved.imports || [],
    calendar: { closures: [], ...(saved.calendar || {}) },
    results: (saved.results || [])
      .map((r) => ({
        ...r,
        label:
          r.label === "Pas joignable"
            ? "Pas de réponse / Non joignable"
            : r.label,
      })),
    tasks: saved.tasks || [],
    needs: (saved.needs || []).map((n) => ({
      ...n,
      status: n.status?.includes("signé")
        ? "signé"
        : n.status || "devis à créer",
      clientId:
        n.clientId ??
        saved.clients.find((c) => c.name === n.client)?.id ??
        null,
    })),
    pendingResultClientId: saved.pendingResultClientId ?? null,
  };
  if (!saved.signals)
    for (const c of state.clients)
      if (c.signal)
        state.signals.push({
          id: `signal-${c.id}`,
          establishment_id: c.id,
          clientId: c.id,
          source: c.source || "Source historique",
          date: dateValue(c.signalDate) || todayParis(),
          job: c.signal,
          qty: null,
          content: c.signal,
          importedAt: stamp(),
          status: "nouveau",
        });
  return recalculateScores(state);
}
export function matchBySiret(clients, siret) {
  return clients.find(
    (c) =>
      String(c.siret).replace(/\s/g, "") === String(siret).replace(/\s/g, ""),
  );
}
export function logCommercialEvent(
  state,
  clientId,
  type,
  label,
  data = {},
  now = new Date(),
) {
  const c = getClient(state, clientId),
    event = {
      id: uid(),
      clientId,
      establishment_id: clientId,
      type,
      label,
      data,
      createdAt: stamp(now),
      ownerId: c.ownerId,
      agency: c.agency,
      source:
        state.signals.filter((s) => s.clientId === clientId).at(-1)?.source ||
        "",
      sector: c.sector,
    };
  return recalculateScores(
    updateClient({ ...state, events: [...state.events, event] }, clientId, {
      history: [
        ...c.history,
        `${formatDate(todayParis(now))} ${new Date(now).toLocaleTimeString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" })} — ${label}`,
      ],
      lastActionAt: event.createdAt,
    }),
    now,
  );
}
export function scheduleWorkingDayTask(
  state,
  clientId,
  title,
  due,
  category = "follow",
  extra = {},
  now = new Date(),
) {
  const c = getClient(state, clientId),
    date = dateValue(due);
  if (isBlockedForProspecting(c) && category !== "manager") throw new Error("La prospection est bloquée pour cet établissement.");
  if (!title?.trim() || !date)
    throw new Error("Objet et échéance obligatoires.");
  const working = addBusinessDays(date, 0, state.calendar.closures);
  const task = {
    ...extra,
    id: uid(),
    clientId,
    title: title.trim(),
    category,
    due_date_original: working,
    scheduled_date: working,
    postponement_count: 0,
    done: false,
    createdAt: stamp(now),
    ownerId: c.ownerId,
    agency: c.agency,
  };
  const next = logCommercialEvent(
    { ...state, tasks: [...state.tasks, task] },
    clientId,
    "task_created",
    `Action « ${task.title} » générée pour le ${formatDate(working)}`,
    { taskId: task.id },
    now,
  );
  return recalculateScores(
    updateClient(next, clientId, { nextAction: working }),
    now,
  );
}
export function rolloverTasks(state, today = todayParis()) {
  let changed = false;
  const scheduled = addBusinessDays(today, 0, state.calendar?.closures || []);
  const tasks = state.tasks.map((t) => {
    if (t.done || t.due_date_original >= today) return t;
    const count = businessDaysBetween(
      t.due_date_original,
      scheduled,
      state.calendar?.closures || [],
    );
    if (t.scheduled_date === scheduled && t.postponement_count === count)
      return t;
    changed = true;
    return { ...t, scheduled_date: scheduled, postponement_count: count };
  });
  return changed ? { ...state, tasks } : state;
}
export function completeTask(state, id, now = new Date()) {
  const t = state.tasks.find((t) => t.id === id);
  if (!t || t.done) return state;
  if (
    t.category === "call" ||
    t.category === "contact" ||
    t.category === "quote"
  )
    throw new Error("Cette action doit être traitée dans son parcours dédié.");
  let next = {
    ...state,
    tasks: state.tasks.map((x) =>
      x.id === id ? { ...x, done: true, completedAt: stamp(now) } : x,
    ),
    engagements: state.engagements.map((e) =>
      e.taskId === id ? { ...e, done: true, completedAt: stamp(now) } : e,
    ),
  };
  next = logCommercialEvent(
    next,
    t.clientId,
    "task_completed",
    `Action terminée : ${t.title}`,
    {},
    now,
  );
  if (t.title === "Envoyer plaquette")
    next = markBrochureSent(next, t.clientId, t.notes || "", now);
  return next;
}
export function createSignal(state, clientId, data, now = new Date()) {
  getClient(state, clientId);
  const signal = {
    ...data,
    id: uid(),
    signal_id: uid(),
    establishment_id: clientId,
    clientId,
    source: data.source?.trim() || "Source non renseignée",
    date: dateValue(data.date) || todayParis(now),
    importedAt: stamp(now),
    status: "nouveau",
  };
  signal.signal_id = signal.id;
  return logCommercialEvent(
    { ...state, signals: [...state.signals, signal] },
    clientId,
    "signal_created",
    `Nouveau signal : ${signal.content || signal.job} · ${signal.source} · ${formatDate(signal.date)}`,
    { signalId: signal.id },
    now,
  );
}
export function reactivateFormerClient(state, clientId, now = new Date()) {
  const c = getClient(state, clientId);
  const lost = c.status === "CLIENT PERDU" || c.status === "À RECONQUÉRIR";
  const next = updateClient(state, clientId, {
    status: lost ? "À RECONQUÉRIR" : "À RÉACTIVER",
    reactivationStatus: "nouveau signal",
    reactivatedAt: stamp(now),
  });
  return logCommercialEvent(
    next,
    clientId,
    lost ? "reconquest" : "reactivation",
    lost
      ? "Nouveau signal : client placé en reconquête, motif de perte conservé"
      : "Réactivation commerciale automatique : nouveau signal",
    {},
    now,
  );
}
export function reactivationInactivity(client, today = todayParis()) {
  const currentDay = parisDateValue(today);
  if (client.kind === "prospect" || !currentDay) return null;
  const end = new Date(`${currentDay}T12:00:00Z`);
  const start = new Date(end);
  start.setUTCFullYear(start.getUTCFullYear() - 1);
  // Clamp leap day to the last day of February.
  if (start.getUTCMonth() !== end.getUTCMonth()) start.setUTCDate(0);
  const from = start.toISOString().slice(0, 10);
  const orderDays = [
    ...new Set((client.orders || []).map(orderDateValue).filter(Boolean)),
  ];
  const lastOrder = orderDays
    .filter((day) => day >= from && day <= currentDay)
    .sort()
    .at(-1);
  if (!lastOrder) return null;
  const days = Math.floor((end.getTime() - new Date(`${lastOrder}T12:00:00Z`).getTime()) / 86400000);
  return days >= 30 ? {lastOrder, days} : null;
}

// Shared by the prospecting badge, reactivation list and navigation count.
export function reactivationSituation(client, today = todayParis()) {
  if (isBlockedForProspecting(client) || isReactivationDeferred(client, today)) return null;
  const inactivity = reactivationInactivity(client, today);
  const risks = [...new Set([client.riskReason, ...(Array.isArray(client.riskReasons) ? client.riskReasons : [])].filter(value => typeof value === 'string' && value.trim()).map(value => value.trim()))];
  const flagged = ["À RÉACTIVER", "CLIENT DORMANT", "À RECONQUÉRIR", "CLIENT PERDU"].includes(client.status);
  if (!inactivity && !flagged && !risks.length) return null;
  return { inactivity, reasons: risks.length ? risks : flagged ? [client.status] : [] };
}
export function reactivationClients(state, today = todayParis()) {
  const unique = new Map();
  for (const client of state.clients) {
    const situation = reactivationSituation(client, today);
    if (!situation) continue;
    const key = String(client.id || client.establishment_id || client.siret || client.name);
    const previous = unique.get(key);
    if (!previous || (situation.inactivity?.days || 0) > (previous.inactivity?.days || 0) ||
      ((situation.inactivity?.days || 0) === (previous.inactivity?.days || 0) && Number(client.score || 0) > Number(previous.client.score || 0)))
      unique.set(key, { client, ...situation });
  }
  return [...unique.values()].sort((a,b) => (b.inactivity?.days || 0) - (a.inactivity?.days || 0) ||
    Number(b.client.score || 0) - Number(a.client.score || 0) || String(a.client.name || '').localeCompare(String(b.client.name || ''),'fr'));
}

export function evaluateReactivation(client, signals, today = todayParis()) {
  if (isBlockedForProspecting(client) || isReactivationDeferred(client, today)) return [];
  const reasons = [];
  const dateMs = (value) => new Date(`${value}T12:00:00Z`).getTime();
  const orders = (client.orders || [])
    .map((order) => ({ ...order, date: orderDateValue(order) }))
    .filter((order) => order.date)
    .sort((a, b) => a.date.localeCompare(b.date));
  const inactivity = reactivationInactivity(client, today);
  if (inactivity) reasons.push(`Absence de commande depuis ${inactivity.days} jours`);
  const recent = orders.filter(
      (o) =>
        (dateMs(today) - dateMs(o.date)) / 86400000 >= 0 &&
        (dateMs(today) - dateMs(o.date)) / 86400000 < 30,
    ),
    previous = orders.filter((o) => {
      const age = (dateMs(today) - dateMs(o.date)) / 86400000;
      return age >= 30 && age < 60;
    });
  for (const [field, label] of [
    ["volume", "volume"],
    ["amount", "CA"],
  ]) {
    const sum = (rows) => rows.reduce((n, o) => n + Number(o[field] || 0), 0),
      before = sum(previous),
      current = sum(recent);
    if (before > 0 && current < before * 0.65)
      reasons.push(
        `Baisse du ${label} de ${Math.round((1 - current / before) * 100)} % par rapport aux 30 jours précédents`,
      );
  }
  if (
    signals.some(
      (s) =>
        s.status === "nouveau" &&
        (dateMs(today) - dateMs(s.date)) / 86400000 <= 30,
    )
  )
    reasons.push("Nouveau signal de recrutement avec provenance identifiée");
  if (client.riskReason) reasons.push(client.riskReason);
  return reasons;
}
export function calculateCommercialScore(client, state, today = todayParis()) {
  const configured=weightedCommercialScore(client,state,today); if(configured)return configured;
  if (isBlockedForProspecting(client)) return {score:0,raw:0,capped:false,factors:[{points:0,label:"Établissement exclu de la prospection"}]};
  const factors = [],
    add = (points, label) => factors.push({ points, label });
  const signal = latestSignal(client, state, today);
  if (signal && isRecruitmentSignal(signal)) {
    const age = Math.floor(
      (Date.parse(today) - Date.parse(signal.date)) / 86400000,
    );
    add(
      25,
      `Signal de recrutement : ${signal.source || "source non renseignée"}`,
    );
    if (age <= 2)
      add(
        10,
        `Signal détecté depuis moins de 48 h (${formatDate(signal.date)})`,
      );
    else if (age <= 7) add(5, "Signal de recrutement de moins de 7 jours");
    else if (age > 90) add(-25, `Signal ancien : ${age} jours`);
    else if (age > 30) add(-10, `Signal ancien : ${age} jours`);
    const posts = signalPosts(signal);
    if (posts > 0)
      add(posts >= 5 ? 10 : posts >= 3 ? 5 : 2, `${posts} postes recherchés`);
  }
  if (client.kind !== "prospect")
    add(
      15,
      client.status === "CLIENT ACTIF"
        ? "Client JM INTERIM actif"
        : "Ancien client JM INTERIM",
    );
  if (
    ["À RÉACTIVER", "CLIENT DORMANT", "À RECONQUÉRIR"].includes(client.status)
  )
    add(5, "Client à réactiver / reconquérir");
  const contact = client.contacts.find((c) => c.active && c.primary);
  if (contact?.name && contact?.role)
    add(10, "Décideur / interlocuteur identifié");
  else
    add(-10, "Informations insuffisantes : interlocuteur ou fonction manquant");
  if (contact?.phone) add(5, "Téléphone direct disponible");
  if (contact?.email) add(5, "Email direct disponible");
  if (!contact?.phone && !contact?.email)
    add(-15, "Informations insuffisantes : aucune coordonnée directe");
  const activeNeeds = state.needs.filter(
    (n) =>
      n.clientId === client.id &&
      !["signé", "perdu", "suspendu"].includes(n.status),
  );
  if (activeNeeds.length) add(10, "Besoin commercial ouvert");
  if (
    activeNeeds.some((n) => /urgent|immédiat|moins de 7/i.test(n.urgency || ""))
  )
    add(10, "Besoin urgent à traiter");
  if (
    state.quotes.some(
      (q) =>
        q.clientId === client.id &&
        ["envoyé", "négociation"].includes(q.status),
    )
  )
    add(10, "Devis en cours");
  const probability =
    client.conversionProbability ?? client.transformationProbability;
  if (probability != null && Number.isFinite(Number(probability))) {
    const percent =
      Number(probability) <= 1
        ? Number(probability) * 100
        : Number(probability);
    if (percent >= 0 && percent <= 100)
      add(
        Math.round(percent / 10),
        `Probabilité de transformation renseignée : ${Math.round(percent)} %`,
      );
  } else if (
    state.quotes.some(
      (q) => q.clientId === client.id && q.status === "négociation",
    )
  )
    add(5, "Transformation : négociation en cours");
  const risk = evaluateReactivation(
    client,
    state.signals.filter((s) => s.clientId === client.id),
    today,
  ).filter((r) => !r.startsWith("Nouveau signal"));
  if (risk.length) add(5, risk[0]);
  if (client.orders.some((o) => o.amount > 0)) add(5, "CA historique connu");
  if (client.orders.some((o) => o.margin > 0))
    add(5, "Marge historique connue");
  const recent = state.results
    .filter(
      (r) =>
        r.clientId === client.id &&
        todayParis(new Date(r.createdAt)) <= today &&
        Date.parse(today) - Date.parse(r.createdAt.slice(0, 10)) <=
          30 * 86400000,
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  let unanswered = 0;
  for (const r of recent) {
    if (!/non joignable|pas de réponse/i.test(r.label)) break;
    unanswered++;
  }
  if (unanswered >= 2)
    add(
      -Math.min(30, unanswered * 5),
      `${unanswered} tentatives consécutives sans réponse (30 derniers jours)`,
    );
  const refused =
    recent.some((r) => /client perdu|pas intéressé|refus/i.test(r.label)) ||
    state.quotes.some(
      (q) =>
        q.clientId === client.id &&
        q.refusedAt &&
        todayParis(new Date(q.refusedAt)) <= today &&
        Date.parse(today) - Date.parse(q.refusedAt.slice(0, 10)) <=
          30 * 86400000,
    );
  if (refused) add(-20, "Refus commercial récent (30 derniers jours)");
  const nextDate = getNextActionDate(client, state);
  if (nextDate && nextDate > today)
    add(-40, `Prochaine action dans le futur : ${formatDate(nextDate)}`);
  if (
    state.needs.some(
      (n) =>
        n.clientId === client.id &&
        (n.status === "suspendu" ||
          (n.postponedUntil && n.postponedUntil > today) ||
          (n.deferredUntil && n.deferredUntil > today)),
    )
  )
    add(-15, "Besoin reporté ou suspendu");
  const interaction = getLastInteraction(client, state, today);
  if (interaction && todayParis(new Date(interaction.at)) === today)
    add(-5, "Interaction commerciale effectuée aujourd’hui");
  const raw = factors.reduce((sum, f) => sum + f.points, 0);
  return {
    score: Math.max(0, Math.min(100, raw)),
    factors,
    raw,
    capped: raw > 100 || raw < 0,
  };
}
export function getScoreReasons(client, state, today = todayParis()) {
  return calculateCommercialScore(client, state, today).factors;
}
export function recalculateScores(state, now = new Date()) {
  const today =
    typeof now === "string" && /^\d{4}-\d{2}-\d{2}$/.test(now)
      ? now
      : todayParis(new Date(now));
  return {
    ...state,
    clients: state.clients.map((c) => {
      const scoring = calculateCommercialScore(c, state, today);
      const riskReasons = evaluateReactivation(
        c,
        state.signals.filter((s) => s.clientId === c.id),
        today,
      );
      return {
        ...c,
        score: scoring.score,
        scoreFactors: scoring.factors,
        scoreCapped: scoring.capped,
        scoreRaw: scoring.raw,
        riskReasons,
      };
    }),
  };
}
export function addContact(
  state,
  clientId,
  data,
  { replace = false, duringCall = false } = {},
  now = new Date(),
) {
  const c = getClient(state, clientId);
  const name =
    data.name || `${data.firstName || ""} ${data.lastName || ""}`.trim();
  if (!name) throw new Error("Le nom du contact est obligatoire.");
  if (replace && data.active === false)
    throw new Error("Le remplaçant doit être un contact actif.");
  const old = c.contacts.find((x) => x.active && x.primary),
    active = data.active !== false,
    primary = active && (replace || data.primary || !old);
  const contact = {
    ...data,
    id: uid(),
    name,
    active,
    primary: !!primary,
    createdAt: stamp(now),
    inactiveReason: active ? "" : data.inactiveReason || "Non renseigné",
    unavailableDays: data.unavailableDays || [],
  };
  let next = updateClient(state, clientId, {
    contacts: [
      contact,
      ...c.contacts.map((x) =>
        replace && x.id === old?.id
          ? {
              ...x,
              active: false,
              primary: false,
              inactiveReason: "A quitté l’établissement",
            }
          : primary
            ? { ...x, primary: false }
            : x,
      ),
    ],
    ...(primary
      ? { contact: name, role: contact.role, phone: contact.phone }
      : {}),
  });
  if (replace && old)
    next = logCommercialEvent(
      next,
      clientId,
      "contact_departed",
      `Contact parti : ${old.name} — ancien contact conservé inactif`,
      {},
      now,
    );
  next = logCommercialEvent(
    next,
    clientId,
    "contact_created",
    `Contact ajouté : ${name}${primary ? " (principal)" : ""}`,
    { contactId: contact.id },
    now,
  );
  if (!contact.phone || !contact.email || !contact.role)
    next = scheduleWorkingDayTask(
      next,
      clientId,
      `Compléter les coordonnées de ${name}`,
      addBusinessDays(todayParis(now), 1, state.calendar.closures),
      "contact",
      { contactId: contact.id },
      now,
    );
  if (replace)
    next = {
      ...next,
      tasks: next.tasks.map((t) =>
        t.clientId === clientId && t.category === "contact" && !t.contactId
          ? { ...t, done: true, completedAt: stamp(now) }
          : t,
      ),
    };
  return recalculateScores(
    {
      ...next,
      pendingResultClientId: duringCall
        ? clientId
        : state.pendingResultClientId,
    },
    now,
  );
}
export function replaceContact(state, clientId, data, now = new Date()) {
  return addContact(
    state,
    clientId,
    data,
    { replace: true, duringCall: true },
    now,
  );
}
export function contactDepartedUnknown(
  state,
  clientId,
  notes = "",
  now = new Date(),
) {
  const c = getClient(state, clientId);
  let next = updateClient(state, clientId, {
    contacts: c.contacts.map((x) =>
      x.primary
        ? {
            ...x,
            active: false,
            primary: false,
            inactiveReason: "A quitté l’établissement",
          }
        : x,
    ),
  });
  next = logCommercialEvent(
    next,
    clientId,
    "contact_departed",
    "Contact parti : remplaçant à rechercher",
    { notes },
    now,
  );
  if (
    !next.tasks.some(
      (t) =>
        !t.done &&
        t.clientId === clientId &&
        t.category === "contact" &&
        !t.contactId,
    )
  )
    next = scheduleWorkingDayTask(
      next,
      clientId,
      "Trouver le nouveau contact",
      addBusinessDays(todayParis(now), 1, state.calendar.closures),
      "contact",
      { notes },
      now,
    );
  return { ...next, pendingResultClientId: clientId };
}
export function updateContact(
  state,
  clientId,
  contactId,
  data,
  now = new Date(),
) {
  const c = getClient(state, clientId),
    old = c.contacts.find((x) => x.id === contactId);
  if (!old) throw new Error("Contact introuvable.");
  const active = data.active ?? old.active,
    primary = active && (data.primary ?? old.primary);
  const contact = { ...old, ...data, active, primary };
  contact.name =
    `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || old.name;
  const contacts = c.contacts.map((x) =>
    x.id === contactId ? contact : primary ? { ...x, primary: false } : x,
  );
  let next = updateClient(state, clientId, {
    contacts,
    ...(primary
      ? { contact: contact.name, phone: contact.phone, role: contact.role }
      : {}),
  });
  if (contact.phone && contact.email && contact.role)
    next = {
      ...next,
      tasks: next.tasks.map((t) =>
        t.contactId === contactId
          ? { ...t, done: true, completedAt: stamp(now) }
          : t,
      ),
    };
  return recalculateScores(
    logCommercialEvent(
      next,
      clientId,
      "contact_updated",
      `Contact actualisé : ${contact.name}`,
      { contactId },
      now,
    ),
    now,
  );
}
export function createNeed(state, clientId, data, now = new Date()) {
  const c = getClient(state, clientId);
  if (
    !data.job?.trim() ||
    !Number.isInteger(Number(data.qty)) ||
    Number(data.qty) < 1 ||
    !contractTypes.includes(data.type)
  )
    throw new Error(
      "Métier, volume entier positif et type de contrat obligatoires.",
    );
  const need = {
    ...data,
    id: uid(),
    clientId,
    establishment_id: clientId,
    client: c.name,
    job: data.job.trim(),
    qty: Number(data.qty),
    contactId: c.contacts.find((x) => x.active && x.primary)?.id,
    status: "devis à créer",
    createdAt: stamp(now),
    ownerId: c.ownerId,
    agency: c.agency,
  };
  let next = logCommercialEvent(
    { ...state, needs: [need, ...state.needs] },
    clientId,
    "need_created",
    `Nouveau besoin : ${need.qty} ${need.job} — ${need.type}`,
    { needId: need.id },
    now,
  );
  next = createSignal(
    next,
    clientId,
    {
      source: "Résultat / saisie du commercial",
      date: todayParis(now),
      job: need.job,
      qty: need.qty,
      content: need.notes || "Nouveau besoin identifié",
      type: "besoin identifié",
    },
    now,
  );
  next = scheduleWorkingDayTask(
    next,
    clientId,
    "Créer le devis",
    todayParis(now),
    "quote",
    { needId: need.id },
    now,
  );
  return recalculateScores(next, now);
}
export function createEngagement(state, clientId, data, now = new Date()) {
  if (!data.title?.trim()) return state;
  const id = uid();
  let next = scheduleWorkingDayTask(
    state,
    clientId,
    data.title,
    data.due,
    "engagement",
    { engagementId: id, notes: data.notes || "" },
    now,
  );
  const t = next.tasks.at(-1);
  return {
    ...next,
    engagements: [
      ...next.engagements,
      {
        id,
        clientId,
        taskId: t.id,
        title: t.title,
        due: t.due_date_original,
        done: false,
        ownerId: getClient(state, clientId).ownerId,
        createdAt: stamp(now),
      },
    ],
  };
}
export function markBrochureSent(
  state,
  clientId,
  notes = "",
  now = new Date(),
) {
  let next = logCommercialEvent(
    state,
    clientId,
    "brochure_sent",
    "Plaquette commerciale envoyée (déclaration manuelle)",
    { notes },
    now,
  );
  next = scheduleWorkingDayTask(
    next,
    clientId,
    "Relancer après plaquette",
    addBusinessDays(todayParis(now), 3, state.calendar.closures),
    "follow",
    { notes },
    now,
  );
  return next;
}
function recordLegacyResult(
  state,
  clientId,
  label,
  details = {},
  now = new Date(),
) {
  if (!resultOptions.includes(label))
    throw new Error(
      "Choisis un résultat final. « Contact parti » est une étape intermédiaire.",
    );
  if (state.pendingResultClientId && state.pendingResultClientId !== clientId)
    throw new Error("Termine d’abord le même appel.");
  if ((details.category === "Autre" || details.lossReason === "Autre" || details.reasons?.includes("Autre")) && !String(details.otherReason || "").trim()) throw new Error("Précise le motif pour le choix Autre.");
  const c = getClient(state, clientId),
    date = todayParis(now),
    due = addBusinessDays(
      details.due ||
        addBusinessDays(
          date,
          label === "Nouveau besoin" ? 14 : 30,
          state.calendar.closures,
        ),
      0,
      state.calendar.closures,
    );
  if (
    ["Client perdu", "Insatisfaction", "Travaille avec un concurrent"].includes(
      label,
    ) &&
    !String(
      details.lossReason || details.category || details.competitor || "",
    ).trim()
  )
    throw new Error(
      "Le motif, la catégorie ou le nom du concurrent est obligatoire.",
    );
  if (label === "Activité en baisse" && !details.reasons?.length)
    throw new Error("Indique une raison de la baisse.");
  const result = {
    ...details,
    id: uid(),
    clientId,
    label,
    createdAt: stamp(now),
    due,
    ownerId: c.ownerId,
    agency: c.agency,
    source:
      state.signals.filter((s) => s.clientId === clientId).at(-1)?.source ||
      c.source ||
      "",
    sector: c.sector,
    deciderJoined: !!details.deciderJoined,
  };
  let next = {
    ...state,
    workLocks: (state.workLocks || []).filter((l) => l.clientId !== clientId),
    pendingResultClientId: null,
    results: [...state.results, result],
    tasks: state.tasks.map((t) =>
      t.clientId === clientId &&
      !t.done &&
      ["call", "follow"].includes(t.category) &&
      t.due_date_original <= date
        ? { ...t, done: true, completedAt: stamp(now) }
        : t,
    ),
  };
  const patch = { lastCallResult: label, lastContactAt: stamp(now) };
  if (label === "Client perdu") {
    patch.status = "CLIENT PERDU";
    patch.lossReason = details.lossReason === "Autre" ? `Autre : ${details.otherReason.trim()}` : details.lossReason;
    patch.lossReasonAt = stamp(now);
  }
  if (label === "Travaille avec un concurrent") {
    patch.status = c.kind === "prospect" ? "PROSPECT" : "À RECONQUÉRIR";
    patch.competitor = {
      name: details.competitor,
      satisfaction: details.satisfaction,
      renewal: details.renewal,
    };
  }
  if (["Activité en baisse", "Insatisfaction"].includes(label)) {
    patch.riskReason = label;
    patch.riskDetails = details;
  }
  next = updateClient(next, clientId, patch);
  next = logCommercialEvent(
    next,
    clientId,
    "call_result",
    `Résultat de mon appel : ${label}${details.otherReason ? " — Autre : " + details.otherReason.trim() : ""}${details.notes ? " — " + details.notes : ""}`,
    { resultId: result.id },
    now,
  );
  if (label === "Nouveau besoin")
    next = createNeed(next, clientId, details, now);
  if (label === "Activité en baisse")
    next = createSignal(
      next,
      clientId,
      {
        source: "Déclaration du commercial",
        date,
        content: details.reasons.join(", "),
        type: "activité en baisse",
      },
      now,
    );
  const title =
    {
      "Nouveau besoin": "Suivre le besoin",
      "Activité en baisse": "Suivi activité",
      Insatisfaction: "Traiter l’insatisfaction",
      "Client perdu": "Reconquête client",
      "Travaille avec un concurrent": "Reconquête concurrent",
    }[label] || label;
  next = scheduleWorkingDayTask(
    next,
    clientId,
    title,
    due,
    "follow",
    { notes: details.notes || "" },
    now,
  );
  if (label === "Insatisfaction" || details.managerTask)
    next = scheduleWorkingDayTask(
      next,
      clientId,
      `Alerte hyperadmin : ${label}`,
      due,
      "manager",
      {},
      now,
    );
  if (details.engagement?.title)
    next = createEngagement(next, clientId, details.engagement, now);
  return recalculateScores(next, now);
}
export function createQuoteVersion(state, needId, data, now = new Date()) {
  const need = state.needs.find((n) => n.id === needId);
  if (!need) throw new Error("Besoin introuvable.");
  if (need.status === "signé") throw new Error("Ce besoin est déjà signé.");
  if (Number(data.rate) < 0 || Number(data.coefficient) < 0)
    throw new Error("Taux/coefficient positifs requis.");
  if (
    !data.conditions?.trim() ||
    (!Number(data.rate) && !Number(data.coefficient))
  )
    throw new Error("Renseigne un taux ou un coefficient et les conditions.");
  const versions = state.quotes.filter((q) => q.needId === needId);
  const quote = {
    ...data,
    validUntil: quoteValidityDate(now),
    id: uid(),
    needId,
    clientId: need.clientId,
    version: Math.max(0, ...versions.map((q) => q.version)) + 1,
    status: "créé",
    job: need.job,
    qty: need.qty,
    createdAt: stamp(now),
  };
  let next = {
    ...state,
    quotes: [...state.quotes, quote],
    tasks: state.tasks.map((t) =>
      t.needId === needId && t.category === "quote"
        ? { ...t, done: true, completedAt: stamp(now) }
        : t,
    ),
  };
  return logCommercialEvent(
    next,
    need.clientId,
    "quote_created",
    `Devis V${quote.version} créé`,
    { quoteId: quote.id, needId },
    now,
  );
}
export function sendQuote(state, quoteId, now = new Date()) {
  const q = state.quotes.find((q) => q.id === quoteId);
  if (!q || !["créé", "négociation"].includes(q.status))
    throw new Error("Ce devis ne peut pas être déclaré envoyé.");
  if (state.needs.find((n) => n.id === q.needId)?.status === "signé")
    throw new Error("Ce besoin est déjà signé.");
  let next = {
    ...state,
    quotes: state.quotes.map((x) =>
      x.id === quoteId ? { ...x, status: "envoyé", sentAt: stamp(now) } : x,
    ),
    needs: state.needs.map((n) =>
      n.id === q.needId
        ? { ...n, status: "devis envoyé", sentAt: stamp(now) }
        : n,
    ),
  };
  next = logCommercialEvent(
    next,
    q.clientId,
    "quote_sent",
    `Devis V${q.version} déclaré envoyé`,
    { quoteId, needId: q.needId },
    now,
  );
  next = scheduleWorkingDayTask(
    next,
    q.clientId,
    `Relancer le devis V${q.version}`,
    addBusinessDays(todayParis(now), 3, state.calendar.closures),
    "follow",
    { quoteId, needId: q.needId },
    now,
  );
  return recalculateScores(next, now);
}
export function refuseQuote(
  state,
  quoteId,
  reason,
  notes = "",
  now = new Date(),
) {
  const q = state.quotes.find((q) => q.id === quoteId);
  if (
    !q ||
    q.status === "signé" ||
    state.needs.find((n) => n.id === q.needId)?.status === "signé"
  )
    throw new Error("Devis introuvable ou besoin déjà signé.");
  if (!lossReasons.includes(reason))
    throw new Error("Le motif de refus est obligatoire.");
  const otherOpen = state.quotes.some(
    (x) =>
      x.needId === q.needId &&
      x.id !== q.id &&
      !["refusé", "signé"].includes(x.status),
  );
  let next = {
    ...state,
    tasks: state.tasks.map((t) =>
      t.quoteId === quoteId ? { ...t, done: true, completedAt: stamp(now) } : t,
    ),
    quotes: state.quotes.map((x) =>
      x.id === quoteId
        ? {
            ...x,
            status: "refusé",
            refusalReason: reason,
            notes,
            refusedAt: stamp(now),
          }
        : x,
    ),
    needs: state.needs.map((n) =>
      n.id === q.needId && !otherOpen
        ? { ...n, status: "perdu", lossReason: reason, lossReasonAt: stamp(now) }
        : n,
    ),
  };
  next = logCommercialEvent(
    next,
    q.clientId,
    "quote_refused",
    `Devis V${q.version} refusé : ${reason}`,
    { quoteId, notes },
    now,
  );
  return next;
}
export function transmitNeedToRecruitment(
  state,
  needId,
  quoteId,
  now = new Date(),
) {
  const need = state.needs.find((n) => n.id === needId),
    quote = state.quotes.find((q) => q.id === quoteId);
  if (!need || !quote || quote.needId !== needId || quote.status !== "signé")
    throw new Error("Transmission interdite avant signature du devis.");
  if (state.recruitmentNeeds.some((n) => n.needId === needId)) return state;
  const c = getClient(state, need.clientId),
    contact =
      c.contacts.find((x) => x.id === need.contactId) ||
      c.contacts.find((x) => x.active && x.primary);
  const missing = ["start", "duration", "hours", "constraints"].filter(
    (key) => !need[key],
  );
  if (!contact?.phone) missing.push("téléphone contact");
  if (!contact?.email) missing.push("email contact");
  const payload = {
    ...need,
    id: uid(),
    needId,
    establishment_id: c.establishment_id,
    client_id: c.client_id,
    client: c.name,
    siret: c.siret,
    siren: c.siren,
    contact: contact ? { ...contact } : null,
    quote: { ...quote },
    quoteVersion: quote.version,
    conditions: quote.conditions,
    transmittedAt: stamp(now),
    missingFields: missing,
    status: missing.length ? "Informations à compléter" : "À traiter",
  };
  let next = {
    ...state,
    recruitmentNeeds: [...state.recruitmentNeeds, payload],
    needs: state.needs.map((n) =>
      n.id === needId
        ? {
            ...n,
            transmittedAt: payload.transmittedAt,
            signedQuoteId: quoteId,
            missingFields: missing,
          }
        : n,
    ),
  };
  return logCommercialEvent(
    next,
    c.id,
    "recruitment_transmitted",
    "Besoin transmis automatiquement au recrutement (prototype local)",
    { needId, quoteId, version: quote.version, missing },
    now,
  );
}
export function signQuote(state, quoteId, now = new Date()) {
  const q = state.quotes.find((q) => q.id === quoteId);
  if (!q) throw new Error("Devis introuvable.");
  if (q.status === "signé") return state;
  if (!["envoyé", "négociation"].includes(q.status))
    throw new Error("Le devis doit être envoyé avant signature.");
  const need = state.needs.find((n) => n.id === q.needId);
  const client = getClient(state, q.clientId);
  const recovered =
    client.kind === "ancien client" ||
    ["À RÉACTIVER", "CLIENT DORMANT", "À RECONQUÉRIR", "CLIENT PERDU"].includes(
      client.status,
    );
  if (need.status === "signé")
    throw new Error("Une version de ce besoin est déjà signée.");
  let next = {
    ...state,
    quotes: state.quotes.map((x) =>
      x.id === quoteId ? { ...x, status: "signé", signedAt: stamp(now) } : x,
    ),
    needs: state.needs.map((n) =>
      n.id === q.needId ? { ...n, status: "signé", signedAt: stamp(now) } : n,
    ),
    tasks: state.tasks.map((t) =>
      t.needId === q.needId ? { ...t, done: true, completedAt: stamp(now) } : t,
    ),
  };
  next = updateClient(next, q.clientId, {
    status: "CLIENT ACTIF",
    kind: "client",
    reactivationStatus: "gagnée",
    ...(recovered ? { reactivatedAt: stamp(now) } : {}),
  });
  next = logCommercialEvent(
    next,
    q.clientId,
    "quote_signed",
    `Devis V${q.version} signé — opportunité gagnée`,
    { quoteId, needId: q.needId },
    now,
  );
  if(client.kind==='prospect' && !recovered && !state.quotes.some(row=>row.clientId===client.id&&row.signedAt))next=logCommercialEvent(next,client.id,'client_activated','Nouveau client CRM : premier devis signé',{quoteId},now);
  if (recovered)
    next = logCommercialEvent(
      next,
      q.clientId,
      "client_reactivated",
      "Client réactivé : devis signé",
      { quoteId, needId: q.needId },
      now,
    );
  return recalculateScores(
    transmitNeedToRecruitment(next, q.needId, quoteId, now),
    now,
  );
}
export function setNeedStatus(state, needId, status, now = new Date()) {
  if (
    !needStatuses.includes(status) ||
    ["signé", "devis envoyé"].includes(status)
  )
    throw new Error("Utilise le parcours devis pour cet état.");
  const n = state.needs.find((n) => n.id === needId);
  if (!n || n.status === "signé")
    throw new Error("Besoin introuvable ou déjà signé.");
  let next = {
    ...state,
    needs: state.needs.map((x) => (x.id === needId ? { ...x, status } : x)),
  };
  if (status === "négociation")
    next = {
      ...next,
      quotes: next.quotes.map((q) =>
        q.needId === needId && q.status === "envoyé"
          ? { ...q, status: "négociation" }
          : q,
      ),
    };
  return logCommercialEvent(
    next,
    n.clientId,
    "need_status",
    `Besoin : ${status}`,
    { needId },
    now,
  );
}
export function searchEstablishments(clients, query) {
  const clean = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .replace(/[\s.\-()]/g, "");
  const terms = String(query || "").trim().split(/\s+/).map(clean).filter(Boolean);
  return clients.filter(c => {
    const values = [c.name, c.tradeName, c.siret, c.siren, c.city, c.phone, c.email,
      ...(c.contacts || []).flatMap(x => [x.name, x.phone, x.email])].map(clean);
    return terms.every(term => values.some(value => value.includes(term)));
  });
}

export function runReactivationEngine(state, now = new Date()) {
  let next = recalculateScores(state, now);
  for (const c of next.clients) {
    if (c.kind === "prospect" || c.closed || isBlockedForProspecting(c) || isReactivationDeferred(c, todayParis(now))) continue;
    const inactivity = reactivationInactivity(c, todayParis(now));
    const anomalies = inactivity ? [`Absence de commande depuis ${inactivity.days} jours`] : [];
    if (
      !anomalies.length ||
      next.tasks.some(
        (t) =>
          t.clientId === c.id &&
          !t.done &&
          (t.reactivationEngine || ["call", "follow"].includes(t.category)),
      )
    )
      continue;
    next = scheduleWorkingDayTask(
      next,
      c.id,
      "Réactiver le client : aucune commande depuis 30 jours",
      todayParis(now),
      "call",
      { reactivationEngine: true },
      now,
    );
    next = updateClient(next, c.id, { reactivationStatus: "à traiter" });
    next = logCommercialEvent(
      next,
      c.id,
      "risk_detected",
      `Anomalie commerciale : ${anomalies.join(" · ")}`,
      {},
      now,
    );
  }
  return next;
}
export function claimCommercialWork(state, clientId, user, now = new Date()) {
  const locks = state.workLocks || [],
    lock = locks.find((l) => l.clientId === clientId);
  if (
    lock &&
    lock.ownerId !== user.id &&
    new Date(now) - new Date(lock.startedAt) < 2 * 60 * 60 * 1000
  )
    throw new Error(
      `Cet établissement est déjà en cours de traitement par ${lock.owner}.`,
    );
  return {
    ...state,
    workLocks: [
      ...locks.filter((l) => l.clientId !== clientId),
      { clientId, ownerId: user.id, owner: user.name, startedAt: stamp(now) },
    ],
  };
}

// V2 shares new outcomes and central automation while retaining established engine behavior.
export function recordResult(state, clientId, label, data = {}, now = new Date()) {
  validateCallV2(state, clientId, label, data);
  if(data.operationId && state.results.some(row=>row.operationId===data.operationId)) return state;
  const completeNeed = data.job && Number.isInteger(Number(data.qty)) && Number(data.qty)>0 && ["Intérim","Vacation","CDD","CDI"].includes(data.type);
  const next = legacyResultOptions.includes(label) && (label!=="Nouveau besoin" || completeNeed)
    ? recordLegacyResult(state,clientId,label,data,now) : state;
  return applyCallV2(state,next,clientId,label,data,now);
}
